import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../../../context/I18nContext';
import RoomAvatar from '../../../RoomAvatar';
import './AvatarSelectionModal.css';

// List of preset avatar options from public/images/avatars
const PRESET_AVATARS = [
  'ic_abacus.png',
  'ic_applause.png',
  'ic_asianfood.png',
  'ic_bagel.png',
  'ic_bathroom.png',
  'ic_beer.png',
  'ic_bike.png',
  'ic_bomb.png',
  'ic_boxinggloves.png',
  'ic_brain.png',
  'ic_buggrey.png',
  'ic_bugred.png',
  'ic_bullseye.png',
  'ic_bus.png',
  'ic_carpool.png',
  'ic_click.png',
  'ic_code.png',
  'ic_coffee.png',
  'ic_cog.png',
  'ic_coins.png',
  'ic_collaborate.png',
  'ic_creditcard.png',
  'ic_crosshair.png',
  'ic_cutemammal.png',
  'ic_donut.png',
  'ic_dumbbell.png',
  'ic_explosion.png',
  'ic_glasses.png',
  'ic_globe.png',
  'ic_headlights.png',
  'ic_hourglass.png',
  'ic_key.png',
  'ic_lifesaver.png',
  'ic_lightbulb.png',
  'ic_metrics.png',
  'ic_microphone.png',
  'ic_moneybag.png',
  'ic_nosmoking.png',
  // 'ic_office.png', // This one isn't on NetSfere for some reason
  'ic_partyhat.png',
  'ic_pencilhammer.png',
  'ic_pin.png',
  'ic_pizza.png',
  'ic_plane.png',
  'ic_promotag.png',
  'ic_radioactive.png',
  'ic_rocketship.png',
  'ic_run.png',
  'ic_safetycone.png',
  'ic_shark.png',
  'ic_shield.png',
  'ic_shoppingbag.png',
  'ic_skull.png',
  'ic_smoking.png',
  'ic_soapbox.png',
  'ic_target.png',
  'ic_thumbsup.png',
  'ic_thumbsupthumbsdown.png',
  'ic_twogears.png',
  'ic_vip.png',
  'ic_walk.png',
];

const AvatarSelectionModal = ({
  anchorElement,
  onClose,
  onSelectDefault,
  onSelectUpload,
  onSelectPreset,
  selectedOption = null, // 'default' or preset path like '/images/avatars/ic_pizza.png'
  fallbackAvatarUrls = [], // Avatar URLs to show for "default" option
  placeholderText = '?', // Text for placeholder
  roomId = null, // Room ID for test attributes
}) => {
  const { t } = useI18n();
  const modalRef = useRef(null);

  // Calculate position immediately from anchorElement
  const rect = anchorElement?.getBoundingClientRect() || { bottom: 0, right: 0 };
  const position = {
    top: rect.bottom,
    left: rect.right + 10,
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) &&
          anchorElement && !anchorElement.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleDefaultClick = () => {
    // Prevent action if already selected
    if (selectedOption === 'default') {
      onClose();
      return;
    }
    onSelectDefault();
    onClose();
  };

  const handleUploadClick = () => {
    onSelectUpload();
    onClose();
  };

  const handlePresetClick = (avatarPath) => {
    // Prevent action if already selected
    if (selectedOption === avatarPath) {
      onClose();
      return;
    }
    onSelectPreset(avatarPath);
    onClose();
  };

  const modalContent = (
    <div
      ref={modalRef}
      className="avatar-selection-modal"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      <div className="avatar-selection-modal-wrapper">
        {/* Default avatar button - shows fallback member avatars */}
        <button
          className={`avatar-modal-option default-option ${selectedOption === 'default' ? 'selected' : ''}`}
          onClick={handleDefaultClick}
        >
          <RoomAvatar
            avatarUrls={fallbackAvatarUrls}
            placeholderText={placeholderText}
            roomId={roomId}
          />
          <span className="avatar-modal-option-text">{t('ChatEdit.default')}</span>
        </button>

        {/* Upload image button */}
        <button className="avatar-modal-option upload-option" onClick={handleUploadClick}>
          <div className="avatar-modal-icon-wrapper">
            <ion-icon name="image" />
          </div>
          <span className="avatar-modal-option-text">{t('ChatEdit.uploadImage')}</span>
        </button>

        {/* Preset avatars grid */}
        <div className="avatar-modal-presets">
          {PRESET_AVATARS.map((avatar, index) => {
            const avatarPath = `/images/avatars/${avatar}`;
            const isSelected = selectedOption === avatarPath;
            return (
              <button
                key={index}
                className={`avatar-modal-preset-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handlePresetClick(avatarPath)}
                title={avatar.replace('ic_', '').replace('.png', '')}
              >
                <img
                  src={avatarPath}
                  alt={avatar.replace('ic_', '').replace('.png', '')}
                  className="avatar-modal-preset-image"
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Use React Portal to render modal outside the TopBar's DOM hierarchy
  // This escapes the z-index stacking context of .chatview-top-bar-container
  return createPortal(modalContent, document.body);
};

AvatarSelectionModal.propTypes = {
  anchorElement: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSelectDefault: PropTypes.func.isRequired,
  onSelectUpload: PropTypes.func.isRequired,
  onSelectPreset: PropTypes.func.isRequired,
  selectedOption: PropTypes.string, // 'default' or preset path
  fallbackAvatarUrls: PropTypes.arrayOf(PropTypes.string),
  placeholderText: PropTypes.string,
  roomId: PropTypes.string,
};

export default AvatarSelectionModal;
